import { GameState } from './GameState';
import { EventBus, GameEvents } from '../core/EventBus';
import { GameConfig } from '../core/GameConfig';
import { GameData } from '../data/GameData';
import { Utils } from '../core/Utils';
import { EventSystem } from './EventSystem';
import { ScheduleSystem } from './ScheduleSystem';
import { ScriptSystem, scriptSystem } from './ScriptSystem';
import { FanSystem } from './FanSystem';
import { EndingSystem } from './EndingSystem';
import { PlotSystem } from './PlotSystem';
import { SaveSystem } from './SaveSystem';
import { rankSystem } from './RankSystem';
import { achievementSystem } from './AchievementSystem';
import { assistantSystem } from './AssistantSystem';
import { encounterSystem, EncounterOffer } from './EncounterSystem';
import { greetingSystem } from './GreetingSystem';
import { guestCameoSystem } from './GuestCameoSystem';
import { directSignSystem } from './DirectSignSystem';
import { endorsementSystem } from './EndorsementSystem';
import { varietyPromoSystem } from './VarietyPromoSystem';
import { normalNpcSystem } from './NormalNpcSystem';
import { awardSystem } from './AwardSystem';
import { AttributeKey, EventDef } from '../data/types';

/** 单周执行结果（供月度执行弹窗逐周展示，只展示当周、不累积） */
export interface WeekChange {
    label: string;
    delta: string; // 已格式化，含符号，负值以 '-' 开头
}
export interface WeekResult {
    week: number;              // 1-based
    schedName: string;         // 本周行程名
    schedType: string;         // 类别 · 消耗/收益摘要
    description: string;       // 行程文案（叙事描述，audition/空档期无）
    forcedRest: boolean;       // 是否资源不足被强制替换为在家休息
    changes: WeekChange[];     // 属性/资源变动
    event: EventDef | null;    // 本周触发的随机事件（未判定）
    isLast: boolean;           // 是否第4周
    /** 本周片场偶遇（拍摄周掷概率产出；VN 演出玩家选定后由 EncounterSystem.resolve 结算） */
    encounter?: EncounterOffer;
}

/** 阶段审查失败待演（2026-10-21）：演出排干后才落终局。存 player.flags 里，刷新/读档不丢 */
const FLAG_STAGE_FAIL_PENDING = 'FLAG_STAGE_FAIL_PENDING';

/**
 * 游戏主循环引擎
 * 管理回合推进：月底决策日 → 次月4周执行 → 月结算 → 阶段审查
 */
export class GameLoop {
    private static instance: GameLoop;

    /** 是否正在执行行程（月度执行弹窗打开中） */
    private isExecuting = false;

    private constructor() {
        // 剧情系统：监听特殊剧本生命周期信号（幂等；月度 checkPending 在 doFinishMonth）
        PlotSystem.getInstance().init();
    }

    static getInstance(): GameLoop {
        if (!this.instance) {
            this.instance = new GameLoop();
        }
        return this.instance;
    }

    /** 进入月底决策日 */
    enterDecisionDay(): void {
        const state = GameState.getInstance();
        console.log(`[GameLoop] 进入决策日 — 第${state.player.year + 1} 年${state.player.month}月`);
        // 阶段审查与结局判定押后（竞角队列待表态时，2026-11-03 竞角重排批）：玩家先表态——
        // 队列还挂在屏幕上就判阶段失败/退役，玩家连签都来不及签。押后的部分由 finishClosingNight 补做。
        const deferred = this.revealQueuePending();
        // 检查阶段审查
        if (!deferred) this.checkStagePass();
        // 年度奖项颁奖（批 G 二改起这里只含「生涯最后一届」一类当场播的届；其余届含剧情届都在 confirmMonth 结算）
        awardSystem.onMonthStart();
        // 有要先演的颁奖夜 ⇒ 结局判定让路（批 G：40 岁那个月正是要颁生涯最后一届的那个月，
        // 先播完当夜再判退役）。之后由决策页每次显示时调 settleEndingIfDue() 收口，
        // ⛔ 依赖内存标记：中途刷新/读档回到该月时照样能判退役。
        if (awardSystem.unseenCeremony()) {
            EventBus.emit(GameEvents.STATE_CHANGED);
            return;
        }
        // 检查结局（阶段失败的演出还挂着时，终局让路给演出，见 resolvePendingStageFail）
        if (!deferred && !this.hasPendingStageFail() && EndingSystem.getInstance().checkEnding()) return;
        // 通知 UI 进入决策模式
        EventBus.emit(GameEvents.STATE_CHANGED);
    }

    /**
     * 竞角队列是否待表态（月末结转包里有行）：收口夜这一晚的位还没派——
     * 玩家先表态，再派剩下的角色（收口夜先派给 NPC 是「永无提名」那批 bug 的根）。
     */
    private revealQueuePending(): boolean {
        return (GameState.getInstance().player.monthHandover?.rows.length ?? 0) > 0;
    }

    /** 决策页显示且没有颁奖夜要播时补跑结局判定；返回 true = 本局就此结束（调用方⛔ 再往下开面板） */
    settleEndingIfDue(): boolean {
        // 失败演出没演完不许先判别的结局（2026-10-21：终局顺序＝先看完这一场再结）
        if (this.hasPendingStageFail()) return false;
        // 竞角队列待表态：结局判定押后（玩家先表态，押后部分在 finishClosingNight 补做）
        if (this.revealQueuePending()) return false;
        return EndingSystem.getInstance().checkEnding();
    }

    /** 阶段审查失败的演出还在队里（终局已排期，等它演完） */
    hasPendingStageFail(): boolean {
        return GameState.getInstance().player.flags[FLAG_STAGE_FAIL_PENDING] === true;
    }

    /**
     * 失败演出排干后落终局（`StageStoryPanel` 的出口；`app.show('decision')` 也查一次，
     * 兜住「事件已演完但标记没兑现就刷新」那一帧）。返回 true＝本局就此结束。
     * 阶段号从 `currentStage` 现读：失败分支不推进阶段，所以它仍是失败那一段。
     */
    resolvePendingStageFail(): boolean {
        const state = GameState.getInstance();
        if (!this.hasPendingStageFail()) return false;
        state.setFlag(FLAG_STAGE_FAIL_PENDING, false);
        const stage = GameData.getStageById(state.currentStage);
        if (!stage) return false;
        EndingSystem.getInstance().triggerStageFail(stage);
        return true;
    }

    /** 确认决策，进入月度执行弹窗（不再自动推进，改由 UI 逐周调用 runWeek） */
    confirmMonth(): void {
        if (this.isExecuting) return;
        // 月初先公布该届提名名单（定格月 = 颁奖月的前一个自然月；⛔ 本届时内部直接返回）
        awardSystem.publishNominations();
        // 再结算本月到期的颁奖夜（2026-09-30 批 G：颁奖夜与收益一起挪到「行程开始前」播，
        // 让玩家 12 月月初听入围、1 月点确认时看颁奖，中间隔着完整的 12 月）
        awardSystem.onMonthConfirm();
        // 月初日历扫描（2026-10-17）：日历事件「当月写当月演」。doFinishMonth 的 checkPending 在
        // advanceMonth 之前跑，会把日历事件推到下个月上屏（女团解散实测），故月在月初这里先扫一遍。
        // 只扫 trigger.calendar；钩子/stage/月底 message 投递仍归 doFinishMonth 的 checkPending。
        PlotSystem.getInstance().checkCalendarAtMonthStart();
        // 月初的开播信号（2026-11-03 茄子令：月初事件里**特殊剧情在前、通告通用事件在后**）：
        // 上面那条扫完日历/特殊剧情，这里才发 premiere，同一个决策页演出档里两类的排队顺序由发射顺序定，
        // 不靠宿主各写一遍。卡片在点「确认行程」之后、行程执行之前演（宿主 `decision`）。
        scriptSystem.notifyPremiereMonth();
        this.isExecuting = true;
        GameState.getInstance().player.week = 1;
    }

    /**
     * 执行当前周行程并返回结果（供月度执行弹窗逐周展示）
     * 捕获执行前后属性/资源差异，触发随机事件（事件效果延后到玩家选择时应用），周次 +1
     */
    runWeek(): WeekResult {
        const state = GameState.getInstance();
        const week = state.player.week;            // 1-based
        const weekIdx = week - 1;
        const slot = state.player.schedule[weekIdx] ?? null;

        // ---- 执行前快照 ----
        const before = this.snapshot();

        // ---- 执行行程 ----
        let forceNote: string | null = null;
        if (slot) {
            forceNote = ScheduleSystem.getInstance().executeSchedule(slot);
        }

        // 资源不足兜底时，执行过程中已把该周行程替换为「在家休息」，重新读取以展示真实执行的行程
        const executedSlot = state.player.schedule[weekIdx] ?? slot;

        // ---- 变动摘要：常规行程按行程定义计算（避免属性上限 clamp 吞掉变化、
        // 保证属性行稳定显示），试镜/签约通告等无固定定义的用执行前后 diff ----
        const changes = this.computeChanges(executedSlot, before, this.snapshot());

        // ---- 触发随机事件（不自动判定，交由 UI 展示选项后执行；只可能在匹配行程的周触发） ----
        const event = EventSystem.getInstance().rollRandomEvent(executedSlot);

        // ---- 通告拍摄周：片场偶遇（按咖位掷概率、池内抽 1）----
        // 2026-10-16 茄子令：随机事件与偶遇彻底分家，同一周可以两样都有，各自独立掷点。
        // 旧写法在这道门上还挂着「同周已有事件就不偶遇」（怕两场演出打架）；两场戏现在共用同一张 VN 卡
        // 串行播：事件读毕再轮到偶遇（收口出口见 MonthProgressPanel.finishWeekContent），不再互相让位。
        // 同日撤掉首拍周保底：这里不再有「盖章」动作（claim 与 contract.encounterGranted 一起删），
        // roll 仍不写状态（2026-10-10 甲案的形状留着），剧情初遇那条只负责派发入队。
        let encounter: EncounterOffer | undefined;
        if (slot?.kind === 'work' && slot.contractId && !forceNote) {
            const plan = encounterSystem.roll(slot.contractId);
            if (plan.kind === 'offer') {
                encounter = plan.offer;
            } else if (plan.kind === 'special') {
                if (!PlotSystem.getInstance().fireByEvent(plan.eventId)) {
                    console.warn(`[Encounter] 特殊 NPC ${plan.npcId} 的初遇事件 ${plan.eventId} 派发被退货，本周不演`);
                }
            }
        }

        // ---- 行程名 / 类别摘要 / 文案 ----
        const { schedName, schedType, description } = this.describeSlot(executedSlot, forceNote);

        // 推进到下一周（结算在 finishMonth）
        state.player.week++;

        return {
            week,
            schedName,
            schedType,
            description,
            forcedRest: forceNote !== null,
            changes,
            event,
            isLast: week >= GameConfig.WEEKS_PER_MONTH,
            encounter,
        };
    }

    /** 月结算 + 进入决策日（4 周执行完毕后由 UI 调用） */
    finishMonth(): void {
        this.doFinishMonth();
    }

    /**
     * 5c 竞角队列弹完后的收尾（2026-11-03 竞角重排批）：收口夜乙段派角 ＋ 押后的阶段审查/结局判定。
     * 月结（换月 + 月初掷点 + 存盘）已经跑完——它在队列之前（玩家在第一个弹窗退出时读档落在下月月初，
     * 队列与掷出的成败跟着存档走，原样再弹、不重掷）。
     * 乙段派角＝**玩家表态完了，这一晚剩下的位才派给 NPC**（收口夜不先派给 NPC）。
     */
    finishClosingNight(): void {
        const state = GameState.getInstance();
        state.dispatchClosingRoles();
        // 押后的判定：队列表态完（结转包已清）才补做阶段审查与结局判定
        if (this.revealQueuePending()) return;
        this.checkStagePass();
        if (!this.hasPendingStageFail() && EndingSystem.getInstance().checkEnding()) return;
        EventBus.emit(GameEvents.STATE_CHANGED);
    }

    /** 属性/资源快照 */
    private snapshot(): Record<string, number> {
        const state = GameState.getInstance();
        const snap: Record<string, number> = {};
        GameData.ATTRIBUTE_KEYS.forEach(k => { snap[k] = state.getAttribute(k as AttributeKey); });
        snap.money = state.getMoney();
        snap.energy = state.getEnergy();
        snap.fame = state.getFame();
        return snap;
    }

    /**
     * 计算单周变动摘要
     * - 常规行程（课程/工作/休息，有 ScheduleDef）：全部按行程**定义值**显示（2026-10-05 用户令：
     *   体力满值也要显示恢复、资源触底也要显示扣除——预览口径，与决策页收益预览一致）。
     * - 试镜/签约通告（无 ScheduleDef）：消耗按固定规则显示（体力-20 / 体力-8）。
     * - 其余（强制休息等无行程周）：退回执行前后相差（diff）。
     */
    private computeChanges(
        slot: import('../data/types').ScheduleSlot | null,
        before: Record<string, number>,
        after: Record<string, number>,
    ): WeekChange[] {
        const def = slot ? GameData.SCHEDULE_MAP[slot.refId] : undefined;
        if (def) {
            // 数值变动统一序（2026-10-26 茄子令）：加数值在前、扣数值在后；组内 属性（表序）→ 名气 → 金钱 → 体力，全站同口径
            const pos: WeekChange[] = [];
            const neg: WeekChange[] = [];
            GameData.ATTRIBUTE_KEYS.forEach(k => {
                const v = def.attributeGain?.[k as AttributeKey] ?? 0;
                if (v !== 0) pos.push({ label: GameData.ATTRIBUTE_DEFS[k as AttributeKey].name, delta: `+${v}` });
            });
            if (def.fameGain && def.fameGain > 0) pos.push({ label: '名气', delta: `+${def.fameGain}` });
            if (def.moneyGain > 0) pos.push({ label: '金钱', delta: `+¥${Utils.formatMoney(def.moneyGain)}` });
            if (def.energyRestore > 0) pos.push({ label: '体力', delta: `+${def.energyRestore}` });
            if (def.moneyCost > 0) neg.push({ label: '金钱', delta: `-¥${Utils.formatMoney(def.moneyCost)}` });
            if (def.energyCost > 0) neg.push({ label: '体力', delta: `-${def.energyCost}` });
            return [...pos, ...neg];
        }
        // 试镜/签约通告：固定体力消耗（不随实际扣减钳制，触底也显示全额）
        if (slot?.kind === 'audition') {
            return [{ label: '体力', delta: `-${GameConfig.AUDITION_ENERGY_COST}` }];
        }
        if (slot?.contractId) {
            return [{ label: '体力', delta: `-${GameConfig.CONTRACT_ENERGY_COST}` }];
        }
        return this.diffChanges(before, after);
    }

    /** 计算两次快照差异，格式化为带符号的变动列表（统一序：加在前扣在后，组内 属性→名气→金钱→体力） */
    private diffChanges(before: Record<string, number>, after: Record<string, number>): WeekChange[] {
        const pos: WeekChange[] = [];
        const neg: WeekChange[] = [];
        GameData.ATTRIBUTE_KEYS.forEach(k => {
            const d = after[k] - before[k];
            if (d > 0) pos.push({ label: GameData.ATTRIBUTE_DEFS[k as AttributeKey].name, delta: `+${d}` });
            else if (d < 0) neg.push({ label: GameData.ATTRIBUTE_DEFS[k as AttributeKey].name, delta: `${d}` });
        });
        const dm = after.money - before.money;
        if (dm > 0) pos.push({ label: '金钱', delta: `+¥${dm.toLocaleString()}` });
        else if (dm < 0) neg.push({ label: '金钱', delta: `-¥${Math.abs(dm).toLocaleString()}` });
        const df = after.fame - before.fame;
        if (df > 0) pos.push({ label: '名气', delta: `+${df}` });
        else if (df < 0) neg.push({ label: '名气', delta: `${df}` });
        const de = after.energy - before.energy;
        if (de > 0) pos.push({ label: '体力', delta: `+${de}` });
        else if (de < 0) neg.push({ label: '体力', delta: `${de}` });
        return [...pos, ...neg];
    }

    /** 行程名 / 类别摘要 / 文案 */
    private describeSlot(
        slot: import('../data/types').ScheduleSlot | null,
        forceNote: string | null = null,
    ): { schedName: string; schedType: string; description: string } {
        const KIND: Record<string, string> = { course: '训练', work: '工作', rest: '休息', audition: '试镜' };
        if (!slot) return { schedName: '空档期', schedType: '本周无安排', description: '' };
        // 试镜周：入口文案（2026-10-28 改版「一次一组」：先选组再逐角色面；剧本池由 UI 渲染）
        if (slot.kind === 'audition') {
            return {
                schedName: slot.title ?? '试镜',
                schedType: KIND[slot.kind] ?? '试镜',
                description: '挑一个剧组，把合适的角色都面一遍……',
            };
        }
        const def = GameData.SCHEDULE_MAP[slot.refId];
        const name = slot.title ?? def?.name ?? '未知行程';
        const parts: string[] = [KIND[slot.kind] ?? slot.kind];
        // 资源进出不再进类型摘要（2026-10-05 用户报「行程卡体钱扣两遍」）：摘要只留类别归属，
        // 数值由执行后变动行（computeChanges）统一展示——预览口径与结算口径并排=重复
        // 签约通告（2026-10-05 用户令）：卡上显示拍摄进度（x/总周，最后一周=杀青）；行程文案统一补
        if (slot.endorseId) {
            // 品牌活动周（2026-11-02 挚友改工作周）：与拍戏同列「工作」
            parts.push('品牌活动');
        }
        if (slot.contractId) {
            const contract = GameState.getInstance().player.contracts.find(c => c.id === slot.contractId);
            const total = Math.max(1, contract?.shotWeeks ?? 1);
            // 日历化批：进度读合同上的已拍周数；本周正在执行时 done 还没 +1，故显示上「含本周」
            const done = contract ? scriptSystem.shotWeeksDoneOf(contract) : 0;
            const shot = Math.min(total, done + 1);
            parts.push(shot >= total ? '杀青' : `${shot}/${total}`);
        }
        // 资源不足强制休息：描述文案替换为兜底提示（行程名/类别已为「在家休息」）
        const description = forceNote ?? def?.description
            ?? (slot.endorseId
                ? '品牌活动的一周：出席、拍摄、采访，按品牌方的流程走完。'
                : slot.contractId ? '进组拍摄的一周：对词、走位、实拍，一场一场磨。收工的时候，天都已经黑透了。' : '');
        return { schedName: name, schedType: parts.join(' · '), description };
    }

    /** 月结算实现 */
    private doFinishMonth(): void {
        const state = GameState.getInstance();

        // 批6 · 月末生命周期结算（拍摄→播出→完结的月粒度推进；放在池刷新与超期作废之前，
        // 让过气衰减看到最新在途状态、滴灌名气赶上当月咖位晋升检查）：
        // 玩家侧 airing 滴灌/播完结 → NPC 侧在途工作时钟（拍摄 −4 周/月 → 播出滴灌 → 入履历）
        ScriptSystem.getInstance().advanceAiringContracts();
        normalNpcSystem.advanceMonthJobs();
        // 2026-10-24 用户改令：甲案「竞争型咖位锚点曲线月拍」整套作废 ⇒ 这里不再有 advanceRivalCurves。
        // rival 与普通 NPC 走同一条时钟（接戏 → 滴灌 → fame 重算 rank → 过气衰减，全在上一行里）；
        // 她的咖位改由「阶段接戏门槛 → 能接到什么档的戏 → 名气涨多少」自然浮动（软限制的含义）。
        // 奖项提名⛔ 在这里公布：2026-09-30 起挪到定格月月初的 `confirmMonth()`（见 AwardSystem.publishNominations）
        // 咖位晋升检查（2026-09-29 统一档期批修：**必须在季末换新之前**——晋升内部的无参
        // refreshMarket 锚"结算时刻的当前月"，排在换新之后会把刚写好的下季市场覆盖回本季
        // （实测 Y3Q4 又能试镜在播《喜帖》的根因）；挪前=覆盖的是本季市场（无害），换新永远收尾。
        // 附带修正：换新 sweep 从此按晋升后的新咖位派角，晋升不再滞后一季生效）
        const rankBefore = state.player.rank;
        rankSystem.checkRankUp();
        // 晋升二线送本（2026-10-27 茄子令）：首次跨入 Lv.5 固定递晋升专属剧（issuePromotionOffer 内 flag 防掉级重发）
        if (rankBefore < 5 && state.player.rank >= 5) directSignSystem.issuePromotionOffer();
        // 成就判定（紧跟晋升：Lv 达标成就基于晋升后的 rank；跨周目累积）
        achievementSystem.checkMonthly();
        // 剧情系统月度检查（批 2：全局 trigger 扫描 + 剧本钩子延迟队列到期处理；docs/特殊剧本实装设计.md §三）
        PlotSystem.getInstance().checkPending();
        // 档期违约（2026-10-27 滚动排期批：作废期限概念删除，唯一约束=剧级档期；替代原 sweepOverdueContracts）
        ScriptSystem.getInstance().checkScheduleBreach();
        // 剧本池滚动换新（2026-10-27：每季一次 → 每月一次）——先收口派角、再装下月在池货架；
        // 过气衰减保持季节奏（月度换新后单独锚回季末月，⛔ 跟着月度走=衰减速度 ×3）
        if (state.player.month % GameConfig.SCRIPT_REFRESH_INTERVAL === 0) {
            normalNpcSystem.applyObsolescenceDecay();
        }
        state.sweepClosingScripts();   // 收口夜=池满月月末（普通剧按剧各收各的）+ 特殊剧定角夜
        state.refreshMarket();         // 装下月决策日的在池货架（含前 POOL_MONTHS-1 个月入池的剧）
        ScriptSystem.getInstance().resetForMarketRefresh();

        // 粉丝月末推进（剧宣批 v11：anchor=fame×K 收敛+绕锚扰动，随机只在此 roll）
        FanSystem.getInstance().advanceFans();

        // 商务代言月结（2026-10-30 批）：品牌解约判定 → 约满结尾款 → 季末考察期晋升；
        // 解约/晋升/约满不在这里弹——进 EndorsementSystem.noticeQueue 待通报队列，决策页消费（经纪人 VN）
        endorsementSystem.monthlySettle(state.player.year * 12 + state.player.month);
        // 宣传综艺月结（2026-10-31 批）：录制月到点=通告费+作品 heat+名气自动结算，回执进通知队列（经纪人 VN）
        varietyPromoSystem.monthlySettle(state.player.year * 12 + state.player.month);

        // checkPostPenalty（连续 N 月未发动态脱粉惩罚+小助理提醒）已整段删除（2026-10-25 茄子令：
        // 「脱粉惩罚我会改在别的地方实现」）——机制与通知一并摘除，advanceFans 保留
        // 小助理月度播报（杀青/开播）已整段删除（2026-10-25 茄子令「这两个都有别的提醒」）：
        // 杀青=执行页结算卡+通告微博弹窗、开播=底部「N部可宣」角标+剧宣窗口，再口播即语义重复；
        // 咖位晋升/带资解锁/通告作废等业务通知仍走各自系统的口播，与本处无关。

        // 落盘节奏（2026-11-03 茄子令改月存）：这里**不写盘**——此刻本月还没换月（week 还停在第 4 周、
        // 排期还是满的），存下去就是一格「月中态」，读回来会把月结重跑一遍。唯一的月格在下面
        // `advanceMonth` ＋月初掷点之后那一次。

        // 推进到下一月
        state.advanceMonth();

        // NPC 主动打招呼（批次 B）：进入新月的第一时间按咖位+好感掷概率，命中者入招呼队列；
        greetingSystem.roll();
        // 好友客串·方向一（批③）：招呼之后同窗掷邀请（全局每月 20%，命中抽 1 好友邀玩家客串其戏）
        guestCameoSystem.rollInvites();
        // 免试镜直签月初掷点（2026-10-25 直签批）：与招呼同窗（新月第一时间），掷中生成待签 offer
        // 走经纪人聊天消息（通知入档+红点+小助理提路都在 DirectSignSystem.dispatch 里）；
        // 供给=当季市场货架，永不造剧——月初掷点时货架已由上月末换新就位
        directSignSystem.rollMonthly();
        // 商务代言月初掷点（2026-10-30 批）：与直签同窗；掷中落 pendingBrandOffer，
        // 送达链（经纪人 VN+签约弹窗·重档）由决策页入场时消费——强制二选一，至多挂 1 个
        endorsementSystem.rollMonthly();
        // 每月唯一的写盘点（2026-11-03 茄子令改月存）：位置钉在 advanceMonth 与全部月初掷点**之后**——
        // 招呼/客串/直签/品牌这四掷都掷完了才存，读回来不许再掷第二遍（招呼会重发、offer 会重掷）。
        // 那一格里恒 week=1、排期已空（`advanceMonth` 清的）＝「本月决策日」的干净起点。
        SaveSystem.getInstance().save();

        // 通知月结算完成
        EventBus.emit(GameEvents.MONTH_END);
        console.log(`[GameLoop] 月结算完成 — 当前 ${state.player.age} 岁 ${state.player.month} 月，名气 ${state.getFame()}（Lv.${state.player.rank} ${rankSystem.getRankName(state.player.rank)}）`);

        this.isExecuting = false;

        // 进入新的决策日
        this.enterDecisionDay();
    }


    /** 检查阶段审查 */
    private checkStagePass(): void {
        const state = GameState.getInstance();
        const stage = GameData.getStageById(state.currentStage);
        if (!stage) return;

        // 到达阶段结束年龄、且那年的 1 月玩完才判（2026-10-25 茄子令「检查时点放一月底」）：
        // 年龄只在跨年进 1 月时 +1，跨年那次决策日（month=1）先不判，1 月的月度执行结束
        // （advance 后 month=2）才判——立约在 Y1M1 月末，「三年后的今天」＝25 岁那年 1 月末，
        // 日期对称；也与 1 月播的颁奖夜同出「1 月出结果」的节奏。附带效应：每段审查期
        // 比 2026-10-25 前多一个可玩月（36 → 37 个月）。
        if (state.player.age >= stage.endAge && state.player.month >= 2) {
            if (state.player.rank < stage.requiredRank) {
                // 审查未通过：阶段表配了失败剧情就先派发，终局等演出排干（2026-10-21）。
                // 没配（2/3 的文案还没写）或那条事件已经演过 / 数据缺＝当场走旧路结算。
                const failId = stage.failEvent;
                const plot = PlotSystem.getInstance();
                const queued = failId ? plot.fireByEvent(failId) : false;
                const stillQueued = failId
                    ? plot.pendingFor('stageStory').some(m => m.eventId === failId)
                    : false;
                if (failId && (queued || stillQueued)) {
                    state.setFlag(FLAG_STAGE_FAIL_PENDING);
                    SaveSystem.getInstance().save();   // 标记与队列一起落盘，刷新后不许跳过这一场直接结算
                    return;
                }
                EndingSystem.getInstance().triggerStageFail(stage);
            } else {
                // 审查通过 → 进入下一阶段
                state.currentStage++;
                state.stageStartAge = state.player.age;
                // 阶段切换立刻落盘：通过回响在决策日入场才演（app.show('decision') 扫描），
                // 中途刷新不许把阶段退回上一段、让审查再判一次
                SaveSystem.getInstance().save();
                EventBus.emit(GameEvents.STAGE_PASSED, state.currentStage);
                console.log(`[GameLoop] 阶段 ${state.currentStage - 1} 通过，进入阶段 ${state.currentStage}`);
            }
        }
    }

    /** 重置 */
    reset(): void {
        this.isExecuting = false;
    }
}

/** 导出单例 */
export const gameLoop = GameLoop.getInstance();